import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiClient } from './client';
import { ensureFreshApiToken } from '../auth/msal';
import { uploadWithProgress, type UploadProgressHandlers } from './upload';

/**
 * AI document intake for the firm requirement library (T-0154; E11-F08).
 *
 * Contract: `docs/requirement-library/technical/api-requirement-library-intake.md`,
 * implemented in `apps/api/src/routes/requirement-library-intake.ts`.
 * Every route is firm-root under `/api/v1/requirement-library/intake` and
 * needs the template curation authority; withdrawing a source document
 * needs a firm admin.
 *
 *   - A document is uploaded as the request body (`application/octet-stream`)
 *     with its name in the query string. The answer is 202 with a run.
 *   - A run moves created -> parsing -> extracting -> ready_for_review ->
 *     confirmed | cancelled | failed, and is polled until it settles.
 *   - Nothing reaches a draft before `:confirm`. Which candidates a bulk
 *     accept takes is decided by the server (`bulk_acceptable`).
 *   - Every number here (confidence, scores, counts, cost) comes from the
 *     server. The client only counts the decisions it is showing.
 */

const BASE = '/api/v1/requirement-library/intake';

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

export const IntakeDocumentSchema = z.object({
  id: z.string(),
  file_name: z.string(),
  mime_type: z.string().nullable().optional(),
  byte_size: z.coerce.number().nullable().optional(),
  page_count: z.number().int().nullable().optional(),
  /**
   * T-0155: how the pages were found. pdf | word_markers | rendered: real
   * pages. none: the file records no pages (cite by section). sheets: one
   * page per sheet (cite by sheet and row). Absent from older servers.
   */
  page_source: z.string().nullable().optional(),
  uploaded_by: z.string().nullable().optional(),
  uploaded_at: z.string().nullable().optional(),
  withdrawn: z.boolean(),
  content_url: z.string().nullable().optional(),
});
export type IntakeDocument = z.infer<typeof IntakeDocumentSchema>;

const count = z.number().int().nullable().optional();

export const IntakeRunSchema = z.object({
  id: z.string(),
  /** created | parsing | extracting | ready_for_review | confirmed | cancelled | failed */
  status: z.string(),
  /** candidates_found | none_found once the document has been read. */
  outcome: z.string().nullable().optional(),
  document: IntakeDocumentSchema,
  previous_run_id: z.string().nullable().optional(),
  started_by: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string().nullable().optional(),
  extracted_at: z.string().nullable().optional(),
  closed_at: z.string().nullable().optional(),
  closed_by: z.string().nullable().optional(),
  counts: z.object({
    pages: count,
    windows: count,
    /** T-0159: windows read so far, recorded as the document is read. Absent from older servers. */
    windows_done: count,
    failed_windows: count,
    candidates: count,
    duplicates: count,
    low_confidence: count,
    dropped: count,
    accepted: count,
    skipped: count,
    undecided: count,
  }),
  /** embeddings | word_overlap */
  duplicate_check_mode: z.string().nullable().optional(),
  model_id: z.string().nullable().optional(),
  cost_cents: z.number().nullable().optional(),
  error: z
    .object({ code: z.string(), message: z.string().nullable().optional() })
    .nullable()
    .optional(),
  /** T-0156: hand-off to the document reader; retried automatically when it stalls. */
  handoff: z
    .object({
      announce_count: z.number().int().optional(),
      last_announced_at: z.string().nullable().optional(),
      last_announce_failed: z.boolean().optional(),
      attempts: z.number().int().optional(),
      heartbeat_at: z.string().nullable().optional(),
      waiting_for_reader: z.boolean().optional(),
      stalled: z.boolean().optional(),
    })
    .nullable()
    .optional(),
});
export type IntakeRun = z.infer<typeof IntakeRunSchema>;

/**
 * Where a cited passage is (T-0155): a page (PDF, or Word with known pages),
 * a section (a Word file that records no pages), or a sheet row.
 */
export const IntakeLocationSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('page'),
    page: z.number().int(),
    section: z.string().nullable().optional(),
  }),
  z.object({
    kind: z.literal('section'),
    section: z.string().nullable().optional(),
    paragraph: z.number().int().nullable().optional(),
  }),
  z.object({
    kind: z.literal('sheet_row'),
    sheet: z.string(),
    row: z.number().int().nullable().optional(),
  }),
]);
export type IntakeLocation = z.infer<typeof IntakeLocationSchema>;

export const IntakeCitationSchema = z.object({
  /** The stored page the passage is on. Show `location`, not this, to a person. */
  page_number: z.number().int(),
  section_path: z.string().nullable().optional(),
  quote: z.string(),
  start: z.number().int(),
  end: z.number().int(),
  location: IntakeLocationSchema.optional(),
});
export type IntakeCitation = z.infer<typeof IntakeCitationSchema>;

/** exact | wording | meaning; any other value is shown as a plain "similar". */
export const IntakeMatchSchema = z.object({
  item_id: z.string(),
  number: z.string(),
  module_id: z.string().nullable().optional(),
  module_name: z.string(),
  subcategory: z.string().nullable().optional(),
  text: z.string(),
  score: z.number(),
  match_kind: z.string(),
  in_draft: z.boolean().optional(),
});
export type IntakeMatch = z.infer<typeof IntakeMatchSchema>;

export const IntakeInDocumentMatchSchema = z.object({
  ordinal: z.number().int(),
  text: z.string(),
  score: z.number(),
  match_kind: z.string(),
});
export type IntakeInDocumentMatch = z.infer<typeof IntakeInDocumentMatchSchema>;

export const IntakeCandidateSchema = z.object({
  id: z.string(),
  ordinal: z.number().int(),
  text: z.string(),
  confidence: z.number(),
  low_confidence: z.boolean(),
  citations: z.array(IntakeCitationSchema),
  suggested: z.object({
    module_id: z.string().nullable(),
    subcategory: z.string().nullable(),
  }),
  module_id: z.string().nullable(),
  module_name: z.string().nullable().optional(),
  module_prefix: z.string().nullable().optional(),
  subcategory: z.string().nullable(),
  matches: z.array(IntakeMatchSchema),
  similar_in_document: z.array(IntakeInDocumentMatchSchema),
  previously_accepted: z
    .object({
      run_id: z.string().nullable().optional(),
      item_id: z.string().nullable().optional(),
      number: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  has_duplicate: z.boolean(),
  /** undecided | accept | skip */
  decision: z.string(),
  decided_by: z.string().nullable().optional(),
  decided_at: z.string().nullable().optional(),
  bulk_acceptable: z.boolean(),
  result: z
    .object({ item_id: z.string(), number: z.string().nullable().optional() })
    .nullable()
    .optional(),
});
export type IntakeCandidate = z.infer<typeof IntakeCandidateSchema>;

export const IntakeModuleSchema = z.object({
  id: z.string(),
  name: z.string(),
  prefix: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
});
export type IntakeModule = z.infer<typeof IntakeModuleSchema>;

const RunEnvelopeSchema = z.object({ run: IntakeRunSchema });
export const IntakeRunsListSchema = z.object({
  runs: z.array(IntakeRunSchema),
  /** T-0157: the caller may withdraw a source document (firm admins). Absent means no. */
  can_withdraw_sources: z.boolean().optional(),
  /** T-0157: the server clock, sent back as the next `changed_since`. */
  server_time: z.string().optional(),
});
export type IntakeRunsList = z.infer<typeof IntakeRunsListSchema>;

export const IntakeCandidatesSchema = z.object({
  run: IntakeRunSchema,
  low_confidence_threshold: z.number().optional(),
  modules: z.array(IntakeModuleSchema),
  bulk_acceptable_count: z.number().int().optional(),
  candidates: z.array(IntakeCandidateSchema),
});
export type IntakeCandidates = z.infer<typeof IntakeCandidatesSchema>;

const CandidateEnvelopeSchema = z.object({ candidate: IntakeCandidateSchema });

export const BulkAcceptResultSchema = z.object({
  run_id: z.string(),
  accepted: z.number().int(),
  candidate_ids: z.array(z.string()),
  left_undecided: z.number().int(),
});
export type BulkAcceptResult = z.infer<typeof BulkAcceptResultSchema>;

export const IntakeConfirmResultSchema = z.object({
  run_id: z.string(),
  status: z.string(),
  added: z.number().int(),
  skipped: z.number().int(),
  undecided: z.number().int(),
  modules: z.array(
    z.object({
      module_id: z.string(),
      module_name: z.string(),
      added: z.number().int(),
      draft_revision: z.number().int(),
    }),
  ),
  items: z.array(
    z.object({
      candidate_id: z.string(),
      item_id: z.string(),
      number: z.string(),
      module_id: z.string(),
      kept_both_with: z.array(z.string()).optional(),
    }),
  ),
});
export type IntakeConfirmResult = z.infer<typeof IntakeConfirmResultSchema>;

export const CandidateSourceSchema = z.object({
  run_id: z.string(),
  candidate_id: z.string(),
  text: z.string(),
  confidence: z.number(),
  document: IntakeDocumentSchema,
  withdrawn: z.boolean(),
  citations: z.array(IntakeCitationSchema.extend({ page_text: z.string().nullable() })),
});
export type CandidateSource = z.infer<typeof CandidateSourceSchema>;

export const DocumentPageSchema = z.object({
  document: IntakeDocumentSchema,
  page_number: z.number().int(),
  section_path: z.string().nullable().optional(),
  text: z.string().nullable(),
  withdrawn: z.boolean(),
});
export type DocumentPage = z.infer<typeof DocumentPageSchema>;

const WithdrawResultSchema = z.object({ document: IntakeDocumentSchema });

/**
 * What an AI-extracted requirement records about where it came from
 * (`source_ref` with `source_kind: 'ai_document'`, amendment 01 Decision 33).
 * Written once by the confirm and never changed.
 */
export const AiDocumentSourceRefSchema = z.object({
  document_id: z.string().optional(),
  file_name: z.string().optional(),
  /** Real page numbers only (none for a sheet or a file that records no pages). */
  pages: z.array(z.number().int()).optional(),
  /** T-0155: how the source's pages were found, and where each cited passage is. */
  page_source: z.string().optional(),
  locations: z.array(IntakeLocationSchema).optional(),
  citations: z
    .array(
      z.object({
        page_number: z.number().int(),
        section_path: z.string().nullable().optional(),
        location: IntakeLocationSchema.optional(),
      }),
    )
    .optional(),
  quote: z.string().optional(),
  confidence: z.number().optional(),
  run_id: z.string().optional(),
  candidate_id: z.string().optional(),
  ai_extracted: z.boolean().optional(),
  accepted_by: z.string().optional(),
  accepted_at: z.string().optional(),
});
export type AiDocumentSourceRef = z.infer<typeof AiDocumentSourceRefSchema>;

/** Read a `source_ref` as an AI document citation, or null when it is not one. */
export function aiDocumentSource(sourceRef: unknown): AiDocumentSourceRef | null {
  const parsed = AiDocumentSourceRefSchema.safeParse(sourceRef);
  if (!parsed.success) return null;
  return parsed.data.file_name || parsed.data.run_id ? parsed.data : null;
}

// ---------------------------------------------------------------------------
// Refusals
// ---------------------------------------------------------------------------

function reasonOf(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.code !== 'conflict') return null;
  const reason = err.details?.['reason'];
  return typeof reason === 'string' ? reason : null;
}

/** `details.reason` of an intake conflict, or null for any other error. */
export const intakeConflictReason = reasonOf;

/** True when the firm AI budget for the month is used up (scenario 7). */
export function isBudgetExhausted(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  return (
    err.details?.['code'] === 'ai.budget_exhausted' ||
    (err.status === 429 && err.code === 'rate_limited')
  );
}

export interface RepeatDocument {
  document: IntakeDocument;
  previousRun: IntakeRun;
}

/** The earlier run carried by an `already_processed` conflict (scenario 10). */
export function repeatFromError(err: unknown): RepeatDocument | null {
  if (reasonOf(err) !== 'already_processed') return null;
  const details = (err as ApiError).details ?? {};
  const run = IntakeRunSchema.safeParse(details['previous_run']);
  if (!run.success) return null;
  const document = IntakeDocumentSchema.safeParse(details['document']);
  return { document: document.success ? document.data : run.data.document, previousRun: run.data };
}

/** The run still reading the document, carried by a `run_in_progress` conflict. */
export function runInProgressFromError(err: unknown): IntakeRun | null {
  if (reasonOf(err) !== 'run_in_progress') return null;
  const run = IntakeRunSchema.safeParse((err as ApiError).details?.['run']);
  return run.success ? run.data : null;
}

export interface CandidateRefusal {
  candidateId: string | null;
  ordinal: number | null;
  /** The draft add's own refusal reason, for example `exact_duplicate`. */
  cause: string | null;
  /** The number of the requirement the candidate clashes with, when given. */
  existingNumber: string | null;
}

/** Which candidate stopped a confirm (`candidate_refused`); nothing was added. */
export function candidateRefusalFromError(err: unknown): CandidateRefusal | null {
  if (reasonOf(err) !== 'candidate_refused') return null;
  const details = (err as ApiError).details ?? {};
  const cause = details['cause'];
  const causeObj = cause && typeof cause === 'object' ? (cause as Record<string, unknown>) : {};
  const existing = causeObj['existing'];
  const existingNumber =
    existing && typeof existing === 'object'
      ? (existing as Record<string, unknown>)['number']
      : undefined;
  return {
    candidateId: typeof details['candidate_id'] === 'string' ? details['candidate_id'] : null,
    ordinal: typeof details['ordinal'] === 'number' ? details['ordinal'] : null,
    cause: typeof causeObj['reason'] === 'string' ? causeObj['reason'] : null,
    existingNumber: typeof existingNumber === 'string' ? existingNumber : null,
  };
}

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

function newIdempotencyKey(): string {
  return typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const runPath = (runId: string) => `${BASE}/runs/${encodeURIComponent(runId)}`;
const documentPath = (documentId: string) => `${BASE}/documents/${encodeURIComponent(documentId)}`;

/**
 * Upload a document and start reading it. The file is the body; its name
 * goes in the query string. `idempotencyKey` is reused when the same
 * upload is retried after a lost response.
 *
 * Sent with `XMLHttpRequest` (T-0156) so `progress` can report the bytes
 * sent and the moment the last one left; aborting `signal` aborts it.
 */
export async function startIntakeRun(
  file: File,
  idempotencyKey: string = newIdempotencyKey(),
  signal?: AbortSignal,
  progress?: UploadProgressHandlers,
): Promise<IntakeRun> {
  const out = await uploadWithProgress(`${BASE}/runs`, file, RunEnvelopeSchema, {
    query: { file_name: file.name, ...(file.type ? { mime_type: file.type } : {}) },
    headers: { 'Content-Type': 'application/octet-stream' },
    idempotencyKey,
    signal,
    ...progress,
  });
  return out.run;
}

/** Read a stored document again ("continue" after `already_processed`). */
export function rereadIntakeDocument(documentId: string): Promise<IntakeRun> {
  return apiClient(`${documentPath(documentId)}/runs`, RunEnvelopeSchema, {
    method: 'POST',
    idempotencyKey: newIdempotencyKey(),
  }).then((out) => out.run);
}

export function listIntakeRuns(limit = 50): Promise<IntakeRunsList> {
  return apiClient(`${BASE}/runs`, IntakeRunsListSchema, { query: { limit } });
}

export function getIntakeRun(runId: string): Promise<IntakeRun> {
  return apiClient(runPath(runId), RunEnvelopeSchema).then((out) => out.run);
}

export function getIntakeCandidates(runId: string): Promise<IntakeCandidates> {
  return apiClient(`${runPath(runId)}/candidates`, IntakeCandidatesSchema);
}

export interface PatchCandidateInput {
  runId: string;
  candidateId: string;
  decision?: 'accept' | 'skip' | 'undecided';
  moduleId?: string;
  subcategory?: string;
}

export function patchIntakeCandidate(input: PatchCandidateInput): Promise<IntakeCandidate> {
  const body: Record<string, unknown> = {};
  if (input.decision !== undefined) body['decision'] = input.decision;
  if (input.moduleId !== undefined) body['module_id'] = input.moduleId;
  if (input.subcategory !== undefined) body['subcategory'] = input.subcategory;
  return apiClient(
    `${runPath(input.runId)}/candidates/${encodeURIComponent(input.candidateId)}`,
    CandidateEnvelopeSchema,
    { method: 'PATCH', body, idempotencyKey: newIdempotencyKey() },
  ).then((out) => out.candidate);
}

/** Accept what the server considers new and confident. No body: it decides. */
export function bulkAcceptIntakeCandidates(runId: string): Promise<BulkAcceptResult> {
  return apiClient(`${runPath(runId)}/candidates:bulk-accept`, BulkAcceptResultSchema, {
    method: 'POST',
    idempotencyKey: newIdempotencyKey(),
  });
}

export function confirmIntakeRun(
  runId: string,
  idempotencyKey: string = newIdempotencyKey(),
): Promise<IntakeConfirmResult> {
  return apiClient(`${runPath(runId)}:confirm`, IntakeConfirmResultSchema, {
    method: 'POST',
    idempotencyKey,
  });
}

export function cancelIntakeRun(runId: string): Promise<IntakeRun> {
  return apiClient(`${runPath(runId)}:cancel`, RunEnvelopeSchema, {
    method: 'POST',
    idempotencyKey: newIdempotencyKey(),
  }).then((out) => out.run);
}

export function getCandidateSource(runId: string, candidateId: string): Promise<CandidateSource> {
  return apiClient(
    `${runPath(runId)}/candidates/${encodeURIComponent(candidateId)}/source`,
    CandidateSourceSchema,
  );
}

export function getDocumentPage(documentId: string, pageNumber: number): Promise<DocumentPage> {
  return apiClient(`${documentPath(documentId)}/pages/${pageNumber}`, DocumentPageSchema);
}

export function withdrawIntakeDocument(documentId: string): Promise<IntakeDocument> {
  return apiClient(`${documentPath(documentId)}:withdraw`, WithdrawResultSchema, {
    method: 'POST',
    idempotencyKey: newIdempotencyKey(),
  }).then((out) => out.document);
}

/**
 * Fetch the original file. A browser navigation never sends the bearer
 * header, so the bytes are fetched with it and handed to the caller.
 */
export async function fetchIntakeDocumentContent(document: {
  id: string;
  file_name: string;
}): Promise<{ blob: Blob; fileName: string }> {
  const correlationId =
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : `cid-${Date.now()}`;
  const headers: Record<string, string> = { 'X-Correlation-Id': correlationId };
  const token = await ensureFreshApiToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  let res: Response;
  try {
    res = await fetch(`${documentPath(document.id)}/content`, { method: 'GET', headers });
  } catch (err) {
    throw new ApiError(
      0,
      'network',
      err instanceof Error ? err.message : 'Network error.',
      correlationId,
    );
  }
  if (!res.ok) {
    let body: { error?: { code?: string; message?: string; details?: Record<string, unknown> } };
    try {
      body = (await res.json()) as typeof body;
    } catch {
      body = {};
    }
    throw new ApiError(
      res.status,
      body.error?.code ?? 'internal',
      body.error?.message ?? `The download failed with status ${res.status}.`,
      correlationId,
      body.error?.details,
    );
  }
  return { blob: await res.blob(), fileName: document.file_name };
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const intakeKeys = {
  all: ['requirement-library-intake'] as const,
  runs: () => ['requirement-library-intake', 'runs'] as const,
  run: (runId: string) => ['requirement-library-intake', 'run', runId] as const,
  candidates: (runId: string) => ['requirement-library-intake', 'candidates', runId] as const,
  source: (runId: string, candidateId: string) =>
    ['requirement-library-intake', 'source', runId, candidateId] as const,
  page: (documentId: string, page: number) =>
    ['requirement-library-intake', 'page', documentId, page] as const,
};

const IN_PROGRESS = new Set(['created', 'parsing', 'extracting']);

/** True while the worker still has the document. */
export function isRunInProgress(status: string): boolean {
  return IN_PROGRESS.has(status);
}

/**
 * Delay before poll number `attempt` (from 0) of a run that is still being
 * read: quick at first, because a short document is done in seconds, then
 * backing off to a steady 8 seconds for a long one.
 */
export function intakePollDelayMs(attempt: number): number {
  const steps = [1000, 1500, 2000, 2500, 3000, 3000, 4000, 5000, 6000];
  return steps[Math.min(attempt, steps.length - 1)] ?? 8000;
}

export const INTAKE_POLL_MAX_MS = 8000;

/** One run, polled while it is in progress. `pollDelay` is injectable for tests. */
export function useIntakeRunQuery(
  runId: string | null,
  pollDelay: (attempt: number) => number = intakePollDelayMs,
) {
  return useQuery({
    queryKey: intakeKeys.run(runId ?? ''),
    queryFn: () => getIntakeRun(runId!),
    enabled: Boolean(runId),
    staleTime: 0,
    refetchInterval: (query) => {
      const run = query.state.data;
      if (!run || !isRunInProgress(run.status)) return false;
      // `dataUpdateCount` grows by one per answer: the attempt number.
      const attempt = Math.max(0, query.state.dataUpdateCount - 1);
      return attempt >= 9 ? INTAKE_POLL_MAX_MS : pollDelay(attempt);
    },
  });
}

/**
 * Past and in-progress runs, with whether the caller may withdraw a source
 * document. Polled while any of them is still being read.
 */
export function useIntakeRunsQuery(enabled: boolean) {
  return useQuery({
    queryKey: intakeKeys.runs(),
    queryFn: () => listIntakeRuns(),
    enabled,
    staleTime: 0,
    refetchInterval: (query) =>
      (query.state.data?.runs ?? []).some((r) => isRunInProgress(r.status)) ? 5000 : false,
  });
}

export function useIntakeCandidatesQuery(runId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: intakeKeys.candidates(runId ?? ''),
    queryFn: () => getIntakeCandidates(runId!),
    enabled: enabled && Boolean(runId),
    staleTime: 0,
  });
}

export function useCandidateSourceQuery(runId: string | null, candidateId: string | null) {
  return useQuery({
    queryKey: intakeKeys.source(runId ?? '', candidateId ?? ''),
    queryFn: () => getCandidateSource(runId!, candidateId!),
    enabled: Boolean(runId) && Boolean(candidateId),
    // A candidate's citations never change; only a withdrawal does.
    staleTime: 60_000,
  });
}

export function useDocumentPageQuery(documentId: string | null, pageNumber: number | null) {
  return useQuery({
    queryKey: intakeKeys.page(documentId ?? '', pageNumber ?? 0),
    queryFn: () => getDocumentPage(documentId!, pageNumber!),
    enabled: Boolean(documentId) && pageNumber != null && pageNumber >= 1,
    staleTime: 60_000,
    retry: false,
  });
}

/** A decision, module or sub-category change: the returned candidate replaces the cached one. */
export function usePatchCandidateMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: patchIntakeCandidate,
    onSuccess: (candidate, input) => {
      qc.setQueryData<IntakeCandidates>(intakeKeys.candidates(input.runId), (prev) =>
        prev
          ? {
              ...prev,
              candidates: prev.candidates.map((c) => (c.id === candidate.id ? candidate : c)),
            }
          : prev,
      );
    },
  });
}

export function useBulkAcceptMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: bulkAcceptIntakeCandidates,
    onSuccess: (_result, runId) => qc.invalidateQueries({ queryKey: intakeKeys.candidates(runId) }),
  });
}

export function useCancelRunMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: cancelIntakeRun,
    onSuccess: (run) => {
      qc.setQueryData(intakeKeys.run(run.id), run);
      void qc.invalidateQueries({ queryKey: intakeKeys.runs() });
    },
  });
}

export function useWithdrawDocumentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: withdrawIntakeDocument,
    onSuccess: () => qc.invalidateQueries({ queryKey: intakeKeys.all }),
  });
}
